/**
 * =====================================================================
 * IMPORTANT - READ BEFORE MODIFYING ANY LIST ENDPOINT IN THIS FILE:
 * =====================================================================
 *
 * TNN's Django backend paginates ALL list endpoints by default:
 *
 *   API returns: { "count": N, "results": [...actual data...] }
 *   baseApi unwraps to: { "count": N, "results": [...] }  (NOT an array)
 *
 * Every ``build.query<Type[], ...>`` endpoint MUST have:
 *
 *   transformResponse: (r: { results?: Type[] }) =>
 *     Array.isArray(r?.results) ? r.results : [],
 *
 * Without this, ``.map()`` on the paginated object either crashes
 * ("TypeError: x.map is not a function") or silently shows nothing.
 * This was the #1 bug class in the v1.6 and v4.0.1 QA reports.
 * =====================================================================
 */
/**
 * Auth API endpoints.
 *
 * Login + token refresh + current user fetch.
 *
 * Note: the login endpoint is NOT scoped to a tenant the way other endpoints
 * are - it has to work BEFORE we have a JWT. The backend's TenantResolutionMiddleware
 * exempts the login path (see TENANT_EXEMPT_PATHS). The tenant_slug is passed
 * in the request body for disambiguation when the same email exists at
 * multiple tenants.
 */
import { baseApi } from "@/app/store/api/baseApi";

import type { AuthUser } from "@/features/auth/authSlice";
import {
  mapQaAccountGroups,
  type QaAccountGroup,
} from "@/features/auth/qa/qaAccounts";

export interface ForgotPasswordResponse {
  success: boolean;
  message: string;
  hint?: "pending_invitation";
}

export interface LoginRequest {
  email: string;
  password: string;
  tenant_slug?: string;
}

export interface LoginResponse {
  access: string;
  refresh: string;
  user: AuthUser;
}

/** RTK Query endpoints for authentication (login, refresh, password reset). */
export const authApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    login: build.mutation<LoginResponse, LoginRequest>({
      query: (body) => ({
        url: "/auth/login/",
        method: "POST",
        body,
      }),
    }),
    refreshToken: build.mutation<{ access: string }, { refresh: string }>({
      query: (body) => ({
        url: "/auth/refresh/",
        method: "POST",
        body,
      }),
    }),
    getCurrentUser: build.query<AuthUser, void>({
      query: () => "/auth/me/",
      providesTags: ["User"],
    }),

    /**
     * GET /api/v1/auth/qa-accounts/
     *
     * Staging-only catalog of active users for the Quick Login panel.
     * Not paginated - returns { groups: [...] } inside the success envelope.
     */
    getQaAccounts: build.query<QaAccountGroup[], void>({
      query: () => "/auth/qa-accounts/",
      transformResponse: (r: { groups?: Parameters<typeof mapQaAccountGroups>[0] }) =>
        mapQaAccountGroups(r?.groups),
      keepUnusedDataFor: 3600,
    }),

    /**
     * POST /api/v1/auth/forgot-password/
     *
     * Always returns 200 with a generic "if an account exists, we
     * sent an email" message - the backend never confirms whether
     * the email is registered, to prevent enumeration. The frontend
     * shows the same success UI either way.
     */
    forgotPassword: build.mutation<
      ForgotPasswordResponse,
      { email: string; tenant_slug?: string }
    >({
      query: (body) => ({
        url: "/auth/forgot-password/",
        method: "POST",
        body,
      }),
    }),

    /**
     * POST /api/v1/auth/reset-password/
     *
     * Consumes a one-time token (from the email link) and sets the
     * new password. Returns 410 GONE if the token is unknown,
     * expired, or already used - the FE treats all three the same
     * way: "this link is no longer valid, request a fresh one."
     */
    resetPassword: build.mutation<
      { success: boolean; message: string },
      { token: string; new_password: string }
    >({
      query: (body) => ({
        url: "/auth/reset-password/",
        method: "POST",
        body,
      }),
    }),
    // R11.F2 - server-side token blacklist on logout.
    // Invalidates the refresh token at the backend so it can't be replayed
    // after the user logs out. The access token remains valid until its 12hr
    // TTL but cannot be extended. Matches backend: BLACKLIST_AFTER_ROTATION=True
    // already configured in SIMPLE_JWT settings.
    blacklistToken: build.mutation<void, { refresh: string }>({
      query: (body) => ({
        url: "/auth/token/blacklist/",
        method: "POST",
        body,
      }),
    }),

    getDisclaimerStatus: build.query<{ accepted: boolean }, void>({
      query: () => "/auth/disclaimer/",
    }),
    acceptDisclaimer: build.mutation<
      { accepted: boolean; disclaimer_accepted_at?: string },
      void
    >({
      query: () => ({
        url: "/auth/disclaimer/",
        method: "POST",
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          authApi.util.updateQueryData("getDisclaimerStatus", undefined, (draft) => {
            draft.accepted = true;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useLoginMutation,
  useRefreshTokenMutation,
  useGetCurrentUserQuery,
  useGetQaAccountsQuery,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useBlacklistTokenMutation,
  useGetDisclaimerStatusQuery,
  useAcceptDisclaimerMutation,
} = authApi;
